import { useEffect, useState } from "react";
import { useBranding } from "@/context/BrandingContext";
import { fileUrl } from "@/lib/api";
import { viewedCount } from "@/lib/recentListings";
import { Button } from "@/components/ui/button";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "rento_install_dismissed";
const MIN_VIEWS = 2; // only nudge after the visitor has browsed a couple of listings

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export function InstallPrompt() {
  const { logo_url, brand_name } = useBranding();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    if (viewedCount() < MIN_VIEWS) return; // earned nudge: wait until a couple of listings browsed

    // An install event may have been captured globally before this mounted
    if (window.__rentoInstall) {
      setDeferred(window.__rentoInstall);
      setVisible(true);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      window.__rentoInstall = e;
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show a gentle hint instead
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (isIos()) {
      setShowIosHint(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.choice;
    } catch {
      /* noop */
    }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-500 sm:inset-x-auto sm:right-5 sm:left-auto"
    >
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-xl shadow-black/10">
        <div className="flex items-start gap-3 p-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#2C3830]">
            <img src={logo_url ? fileUrl(logo_url) : "/icon-192.png"} alt={brand_name || "RENTO"} className="h-full w-full object-contain p-1.5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-semibold text-[#1A1A1A]">Get the {brand_name || "RENTO"} app</p>
            <p className="mt-0.5 text-sm text-[#6B7280]">
              Install RENTO on your device for faster browsing and quick access — no app store needed.
            </p>
          </div>
          <button
            data-testid="install-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="shrink-0 rounded-full p-1 text-[#9CA3AF] transition-colors hover:bg-[#F3F4F6] hover:text-[#1A1A1A]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {showIosHint ? (
          <div data-testid="install-ios-hint" className="border-t border-border/60 bg-[#F7F5F0] px-4 py-3 text-sm text-[#2C3830]">
            <p className="flex flex-wrap items-center gap-1">
              Tap <Share className="mx-0.5 inline h-4 w-4 text-[#D4A373]" /> <strong>Share</strong>, then
              <Plus className="mx-0.5 inline h-4 w-4 text-[#D4A373]" /> <strong>Add to Home Screen</strong>.
            </p>
          </div>
        ) : (
          <div className="flex gap-2 border-t border-border/60 px-4 py-3">
            <Button
              data-testid="install-app-btn"
              onClick={install}
              className="flex-1 rounded-full bg-[#2C3830] text-white hover:bg-[#1f281f]"
            >
              <Download className="mr-2 h-4 w-4" /> Install app
            </Button>
            <Button
              data-testid="install-later-btn"
              onClick={dismiss}
              variant="outline"
              className="rounded-full"
            >
              Not now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
